import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { trpc } from "@editor/utils/trpc";

import { useReactRenderStore } from "@/features/react-editor/store/reactRenderStore";
import { findPageFileFromPath } from "@/features/react-editor/utils/filesUtils";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

interface TipTapOverlayProps {
  initialContent: string;
  className?: string;
  styleOverrides: Record<string, string>;
  page: { pageRoute: string; id: string };
  projectId: string;
}

const TipTapOverlay = forwardRef<HTMLDivElement, TipTapOverlayProps>(
  ({ initialContent, className, styleOverrides, page, projectId }, ref) => {
    const localRef = useRef<HTMLDivElement>(null);
    // NOTE (Bobby, 2025-06-26): Modifiers is not currently used since the webcontainer and this
    // file tree should come from nextJSProject service instead. Kurt is doing the work on
    // modifiers and will be pulling files from nextJSProject service.
    // const { data: files = [], refetch: refetchFiles } = trpc.file.list.useQuery(
    //   projectId ? { projectId } : skipToken,
    // );

    useImperativeHandle(ref, () => localRef.current!, []);

    const editor = useEditor({
      extensions: [StarterKit],
      content: initialContent,
    });

    const { selectedElement, setSelectedElement, setSectionClicked } =
      useReactRenderStore();

    useEffect(() => {
      if (editor) {
        editor.commands.setContent(initialContent);
        // Auto-focus the editor when content is set
        editor.commands.focus();
      }
    }, [initialContent, editor]);

    const { mutate: applyJsxNodeUpdate, isPending } =
      trpc.modifiers.applyJsxNodeUpdate.useMutation({
        onSuccess: () => {
          // void refetchFiles();
          setSelectedElement(null);
          setSectionClicked(false);
        },
      });

    if (!editor) {
      return null;
    }

    const handleEditTextSubmit = () => {
      if (selectedElement && page) {
        const openFile = findPageFileFromPath(page.pageRoute, []);
        if (
          openFile &&
          selectedElement.file &&
          selectedElement.line !== null &&
          selectedElement.column !== null &&
          selectedElement.childText !== null
        ) {
          const originalText = initialContent;

          const updatedText = editor.getText();
          const hasTextChanged = updatedText !== originalText;

          if (hasTextChanged) {
            applyJsxNodeUpdate({
              projectId: projectId ?? "",
              openFile,
              updatedText: hasTextChanged ? updatedText ?? "" : undefined,
              selectedElement: {
                props: selectedElement.props,
                file: selectedElement.file,
                line: selectedElement.line,
                column: selectedElement.column,
                childText: selectedElement.childText,
              },
            });
          }
        }
      }
    };

    return (
      <div
        ref={localRef}
        className="absolute z-[100000]"
        style={{
          pointerEvents: "auto",
          display: "none",
        }}
      >
        <EditorContent
          editor={editor}
          className={className}
          style={{
            ...styleOverrides,
            width: "100%",
            height: "100%",
            backgroundColor: "transparent",
            outline: "none",
            margin: 0,
            padding: 0,
          }}
        />
        <div className="mt-1 flex gap-2 justify-end">
          <button
            className="text-xs text-primary disabled:opacity-50"
            onClick={handleEditTextSubmit}
            disabled={isPending}
          >
            {isPending ? "Saving..." : "Save"}
          </button>
          <button
            className="text-xs text-muted"
            onClick={() => {
              setSelectedElement(null);
              setSectionClicked(false);
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  },
);

TipTapOverlay.displayName = "TipTapOverlay";

export default TipTapOverlay;
